import { useApi } from './useApi';

export function useOrders() {
    const { fetchData, postData, putData, deleteData } = useApi();

    /**
     * Wraps an API call in a standard response envelope.
     * @param {Function} fn       - Async function to call.
     * @param {Function} transform - Optional: reshape the raw result into { ...successProps }.
     *                               Defaults to wrapping the result as { data }.
     */
    const apiCall = async (fn, transform = (result) => ({ data: result })) => {
        try {
            return { success: true, ...transform(await fn()) };
        } catch (err) {
            // When responseType is 'blob', axios wraps the error body as a Blob.
            // We need to read it as text and parse JSON to get the real message.
            let message = err.message || 'Lỗi kết nối';
            let errors = err.response?.data?.errors
                ?? (typeof err.response?.data?.message === 'object' ? err.response?.data?.message : null);

            const responseData = err.response?.data;
            if (responseData instanceof Blob && responseData.type.includes('application/json')) {
                try {
                    const text = await responseData.text();
                    const json = JSON.parse(text);
                    if (json.message) message = json.message;
                    if (json.errors) errors = json.errors;
                } catch (_) { /* keep original message */ }
            }

            return { success: false, error: message, errors };
        }
    };

    // ─── JSON endpoints ────────────────────────────────────────────────────────

    const fetchOrdersApi = (params = {}) => apiCall(() => fetchData('/api/orders', { params }));
    const getOrderDetailApi = (id) => apiCall(() => fetchData(`/api/orders/${id}?include=order_items,customer,labels&searchJoin=AND`));
    const createOrderApi = (payload) => apiCall(() => postData('/api/orders', payload));
    const updateOrderApi = (id, payload) => apiCall(() => putData(`/api/orders/${id}`, payload));
    const changeStatusApi = (payload) => apiCall(() => postData('/api/orders/change-status', payload));
    const resyncOrderPaymentApi = (id) => apiCall(() => postData(`/api/orders/${id}/resync-payment`, {}));
    const buyTrackingApi = (payload) => apiCall(() => postData('/api/orders/request-buy-tracking', payload));
    const calcShippingFeesApi = (payload) => apiCall(() => postData('/api/orders/calc-shipping-fees', payload));
    const addCustomFeeApi = (payload) => apiCall(() => postData('/api/orders/custom-fee', payload));
    const createExpenseApi = (payload) => apiCall(() => postData('/api/expenses', payload));
    const deleteExpenseApi = (id) => apiCall(() => deleteData(`/api/expenses/${id}`));
    const getTrackingDataApi = (id) => apiCall(() => fetchData(`/api/orders/${id}/tracking-data`));

    const importOrdersApi = (file) => {
        const formData = new FormData();
        formData.append('import_file', file);
        return apiCall(() => postData('/api/orders/import', formData));
    };

    // ─── Blob endpoints (return raw response for streaming/download) ────────────

    const exportOrdersApi = (params) => {
        const queryParams = {
            ...params,
            ids: Array.isArray(params.ids) ? params.ids.join(',') : params.ids,
        };
        return fetchData('/api/orders/export', { params: queryParams, responseType: 'blob' });
    };

    const downloadTemplateApi = () =>
        fetchData('/api/orders/excel-template', { responseType: 'blob' });

    const downloadLabelsApi = (payload) =>
        postData('/api/orders/labels/download', payload, { responseType: 'blob' });

    const getPublicLabelApi = (orderCode) =>
        apiCall(() => fetchData(`/api/orders/public-label/${orderCode}`, { responseType: 'blob' }));

    const getPublicLabelImageApi = (orderCode) =>
        apiCall(() => fetchData(`/api/orders/public-label-image/${orderCode}`, { responseType: 'blob' }));

    // ─── Stubs ──────────────────────────────────────────────────────────────────

    // TODO: implement delete endpoint on backend
    const deleteOrderApi = async () => ({ success: true });

    // ─── Public API ─────────────────────────────────────────────────────────────

    return {
        fetchOrdersApi,
        getOrderDetailApi,
        createOrderApi,
        updateOrderApi,
        deleteOrderApi,
        changeStatusApi,
        resyncOrderPaymentApi,
        buyTrackingApi,
        calcShippingFeesApi,
        addCustomFeeApi,
        createExpenseApi,
        deleteExpenseApi,
        getTrackingDataApi,
        importOrdersApi,
        exportOrdersApi,
        downloadTemplateApi,
        downloadLabelsApi,
        getPublicLabelApi,
        getPublicLabelImageApi,
    };
}
